import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/** A Claude-suggested routing for a triage item. */
export interface AssignmentSuggestion {
  clientId: string;
  clientName: string;
  projectId: string;
  projectName: string;
  taskTitle: string;
  /** 0..1 calibrated certainty. */
  confidence: number;
  reasoning: string;
}

interface SuggestInput {
  triageId?: string;
  title?: string;
  source?: string;
}

/** True when Cloud Functions are wired up (live mode). */
export const aiEnabled = Boolean(functions);

/**
 * Ask Claude (via the `suggestTriageAssignment` Cloud Function) which client
 * a triage item should be routed to. Throws on failure so callers can show a
 * fallback manual picker.
 */
export async function suggestTriageAssignment(
  input: SuggestInput,
): Promise<AssignmentSuggestion> {
  if (!functions) throw new Error('AI is unavailable — Cloud Functions are not configured.');
  const callable = httpsCallable<SuggestInput, AssignmentSuggestion>(
    functions,
    'suggestTriageAssignment',
  );
  const { data } = await callable(input);
  return data;
}

/** Outcome of an inbox scan. */
export interface ScanResult {
  /** Triage items newly created (starred mail, Slack, and "$CRM" mail — every
   *  scanned source lands in triage, never as a task directly). */
  added: number;
  /** Messages Claude judged already-present or not actionable. */
  skipped: number;
  /** Messages fetched and considered. */
  scanned: number;
  /** Attachments downloaded and attached to the resulting triage items. */
  attachments: number;
}

/**
 * Ask Claude (via the `scanInboxes` Cloud Function) to read the user's
 * starred emails, "$CRM"-labeled emails, and any connected Slack workspaces,
 * and add the actionable ones to the triage inbox for manual assignment.
 * Nothing is ever created as a task directly — every source lands in triage
 * first. Gmail/Slack access is minted server-side from stored refresh
 * tokens — no token to pass here.
 *
 * A full scan (reading bodies + downloading attachments for every "$CRM"
 * email) can comfortably run past the SDK's 70s default callable timeout, so
 * this is given a longer one matching the function's own `timeoutSeconds`.
 */
export async function scanInboxes(): Promise<ScanResult> {
  if (!functions) throw new Error('AI is unavailable — Cloud Functions are not configured.');
  const callable = httpsCallable<Record<string, never>, ScanResult>(functions, 'scanInboxes', {
    timeout: 300_000,
  });
  const { data } = await callable({});
  return data;
}

/** Exchange a one-time Gmail authorization code for a stored refresh token. */
export async function connectGoogleAccount(code: string, redirectUri: string): Promise<void> {
  if (!functions) throw new Error('AI is unavailable — Cloud Functions are not configured.');
  const callable = httpsCallable<{ code: string; redirectUri: string }, { connected: boolean }>(
    functions,
    'connectGoogleAccount',
  );
  await callable({ code, redirectUri });
}

/** Forget the stored Gmail refresh token. */
export async function disconnectGoogleAccount(): Promise<void> {
  if (!functions) throw new Error('AI is unavailable — Cloud Functions are not configured.');
  const callable = httpsCallable<Record<string, never>, { connected: boolean }>(
    functions,
    'disconnectGoogleAccount',
  );
  await callable({});
}

/** Exchange a one-time Slack authorization code for one workspace's stored
 *  refresh token. */
export async function connectSlackWorkspace(
  code: string,
  redirectUri: string,
): Promise<{ teamId: string; teamName: string }> {
  if (!functions) throw new Error('AI is unavailable — Cloud Functions are not configured.');
  const callable = httpsCallable<
    { code: string; redirectUri: string },
    { teamId: string; teamName: string }
  >(functions, 'connectSlackWorkspace');
  const { data } = await callable({ code, redirectUri });
  return data;
}

/** Forget one connected Slack workspace's stored tokens. */
export async function disconnectSlackWorkspace(teamId: string): Promise<void> {
  if (!functions) throw new Error('AI is unavailable — Cloud Functions are not configured.');
  const callable = httpsCallable<{ teamId: string }, { disconnected: boolean }>(
    functions,
    'disconnectSlackWorkspace',
  );
  await callable({ teamId });
}

/** Tag (or clear, with `clientId: null`) a connected Slack workspace's CRM client. */
export async function setSlackWorkspaceClient(
  teamId: string,
  clientId: string | null,
): Promise<void> {
  if (!functions) throw new Error('AI is unavailable — Cloud Functions are not configured.');
  const callable = httpsCallable<
    { teamId: string; clientId: string | null },
    { clientId: string | null }
  >(functions, 'setSlackWorkspaceClient');
  await callable({ teamId, clientId });
}
